import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { NoteRecord } from '@/types/note';
import {
  groupNotes,
  groupNotesAsSingle,
  type ExamGroup,
  type StudentGroup,
} from '@/utils/noteGroups';
import StudentNotesGroup from './StudentNotesGroup';
import StudentFileList from './StudentFileList';
import PrintArea from './PrintArea';
import PrintGuideModal from './PrintGuideModal';
import NoteSheet from './NoteSheet';
import { exportNotesToPdf } from '@/utils/exportNotesPdf';

interface NotesManagerProps {
  notes: NoteRecord[];
  /** 선생님/운영자는 학생 파일 목록, 학생은 본인 파일 하나만 봅니다. */
  isTeacher: boolean;
  ownerLabel?: string;
  onChangeNote: (id: string, patch: Partial<NoteRecord>) => void;
  onDeleteNote: (id: string) => void;
  onDeleteNotes: (ids: string[]) => void;
  onClearAll: () => void;
}

type ConfirmState =
  | { kind: 'exam'; ids: string[]; label: string }
  | { kind: 'file'; ids: string[]; label: string }
  | { kind: 'all' }
  | null;

export default function NotesManager({
  notes,
  isTeacher,
  ownerLabel = '내 오답노트',
  onChangeNote,
  onDeleteNote,
  onDeleteNotes,
  onClearAll,
}: NotesManagerProps) {
  const groups = useMemo(() => groupNotes(notes), [notes]);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [printQueue, setPrintQueue] = useState<NoteRecord[]>([]);
  const [pendingPrint, setPendingPrint] = useState<NoteRecord[] | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  // PDF 내보내기 상태
  const [pdfQueue, setPdfQueue] = useState<NoteRecord[]>([]);
  const [pdfLabel, setPdfLabel] = useState('오답노트');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const pdfStageRef = useRef<HTMLDivElement>(null);

  const openGroup = useMemo<StudentGroup | null>(() => {
    if (isTeacher) return groups.find((g) => g.key === openKey) ?? null;
    return groupNotesAsSingle(notes, ownerLabel);
  }, [isTeacher, groups, openKey, notes, ownerLabel]);

  // 열려 있던 학생 파일이 삭제되면 목록으로 돌아갑니다.
  useEffect(() => {
    if (isTeacher && openKey && !groups.some((g) => g.key === openKey)) {
      setOpenKey(null);
      setSelectedKeys(new Set());
    }
  }, [isTeacher, openKey, groups]);

  useEffect(() => {
    if (printQueue.length === 0) return;
    const handleAfter = () => setPrintQueue([]);
    window.addEventListener('afterprint', handleAfter);
    const raf = requestAnimationFrame(() => window.print());
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('afterprint', handleAfter);
    };
  }, [printQueue]);

  // PDF 내보내기: 오프스크린 스테이지에 시트가 그려진 뒤 캡처해서 파일로 저장합니다.
  useEffect(() => {
    if (!exporting) return;
    const stage = pdfStageRef.current;
    if (!stage) {
      setExporting(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        await exportNotesToPdf(stage, pdfLabel);
      } catch (err) {
        if (alive) {
          setExportError(err instanceof Error ? err.message : 'PDF를 만들지 못했어요. 다시 시도해 주세요.');
        }
      } finally {
        if (alive) {
          setExporting(false);
          setPdfQueue([]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [exporting, pdfLabel]);

  const toggleInSet = (setter: Dispatch<SetStateAction<Set<string>>>, key: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpand = (key: string) => toggleInSet(setExpandedKeys, key);
  const toggleSelect = (key: string) => toggleInSet(setSelectedKeys, key);

  const examSelKeys = (group: StudentGroup) => group.exams.map((e) => `${group.key}::${e.key}`);

  const toggleAllInGroup = (group: StudentGroup) => {
    const keys = examSelKeys(group);
    const allOn = keys.length > 0 && keys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const selectedNotes = useMemo(() => {
    if (!openGroup) return [];
    return openGroup.exams
      .filter((exam) => selectedKeys.has(`${openGroup.key}::${exam.key}`))
      .flatMap((exam) => exam.notes);
  }, [openGroup, selectedKeys]);

  const openFile = (group: StudentGroup) => {
    setOpenKey(group.key);
    setSelectedKeys(new Set());
    setExpandedKeys(new Set());
  };

  const closeFile = () => {
    setOpenKey(null);
    setSelectedKeys(new Set());
  };

  /** 인쇄 버튼 → 옵션 팝업을 먼저 띄웁니다. */
  const openPrint = (queue: NoteRecord[]) => {
    if (queue.length === 0) return;
    setPendingPrint(queue);
  };

  /** PDF 내보내기 버튼 → 스테이지에 그린 뒤 파일로 저장합니다. */
  const startExport = (queue: NoteRecord[], label: string) => {
    if (queue.length === 0) return;
    setExportError(null);
    setPdfLabel(label);
    setPdfQueue(queue);
    setExporting(true);
  };

  /**
   * 선택한 오답노트에서 학생 이름·시험명을 자동으로 뽑아 파일명 라벨을 만듭니다.
   * 예) 홍길동-기말고사, 홍길동-기말고사 외 2개, 5명-전체
   */
  const buildExportLabel = (queue: NoteRecord[]): string => {
    const students = Array.from(new Set(queue.map((n) => n.studentName.trim()).filter(Boolean)));
    const exams = Array.from(new Set(queue.map((n) => n.examName.trim()).filter(Boolean)));

    const studentPart =
      students.length === 0 ? '오답노트' : students.length === 1 ? students[0] : `${students.length}명`;

    let examPart = '';
    if (exams.length === 1) examPart = exams[0];
    else if (exams.length > 1) examPart = `${exams[0]} 외 ${exams.length - 1}개`;

    return examPart ? `${studentPart}-${examPart}` : studentPart;
  };

  /** 전체 내보내기처럼 여러 학생이 포함될 때는 '전체'임을 앞에 명시합니다. */
  const buildAllLabel = (queue: NoteRecord[]): string => `전체-${buildExportLabel(queue)}`;

  /** 팝업에서 '인쇄하기' → 선택한 방식으로 실제 인쇄를 시작합니다. */
  const confirmPrint = () => {
    if (!pendingPrint) return;
    const queue = pendingPrint;
    setPendingPrint(null);
    setPrintQueue([]);
    // 상태를 비운 뒤 다음 틱에 채워 강제로 다시 렌더 → 인쇄 반영
    requestAnimationFrame(() => setPrintQueue(queue));
  };

  const requestDeleteExam = (group: StudentGroup, exam: ExamGroup) => {
    setConfirm({
      kind: 'exam',
      ids: exam.notes.map((n) => n.id),
      label: `${group.studentName} · ${exam.examName}`,
    });
  };

  const requestDeleteFile = (group: StudentGroup) => {
    setConfirm({
      kind: 'file',
      ids: group.exams.flatMap((exam) => exam.notes.map((n) => n.id)),
      label: group.studentName,
    });
  };

  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === 'all') onClearAll();
    else onDeleteNotes(confirm.ids);
    setConfirm(null);
  };

  const confirmModal = confirm ? (
    <div className="print-hide fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4">
      <div className="w-full max-w-[380px] rounded-lg border border-background-200 bg-white p-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-delete-bin-6-line text-lg"></i>
          </span>
          <h3 className="font-heading text-base font-bold text-foreground-950">
            {confirm.kind === 'all'
              ? '전체 삭제할까요?'
              : confirm.kind === 'file'
                ? '이 학생 파일을 삭제할까요?'
                : '이 시험을 삭제할까요?'}
          </h3>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          {confirm.kind === 'all' ? (
            <>
              이 계정의 오답노트 <span className="font-bold text-foreground-800">{notes.length}개</span>가 모두
              삭제됩니다. 이 작업은 되돌릴 수 없어요.
            </>
          ) : confirm.kind === 'file' ? (
            <>
              <span className="font-bold text-foreground-800">{confirm.label}</span> 학생 파일의 오답노트{' '}
              <span className="font-bold text-foreground-800">{confirm.ids.length}개</span>가 삭제됩니다. 이 작업은
              되돌릴 수 없어요.
            </>
          ) : (
            <>
              <span className="font-bold text-foreground-800">{confirm.label}</span> 시험의 오답노트{' '}
              <span className="font-bold text-foreground-800">{confirm.ids.length}개</span>가 삭제됩니다.
            </>
          )}
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => setConfirm(null)}
            className="flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
          >
            취소
          </button>
          <button
            type="button"
            onClick={runConfirm}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-accent-700"
          >
            <i className="ri-delete-bin-6-line text-base"></i>
            삭제
          </button>
        </div>
      </div>
    </div>
  ) : null;

  /* PDF 내보내기용 오프스크린 스테이지 + 진행/에러 오버레이 */
  const exportLayer = (
    <>
      <div className="pdf-export-stage" ref={pdfStageRef} aria-hidden="true">
        {pdfQueue.map((note) => (
          <div key={note.id} data-pdf-sheet className="pdf-sheet-item">
            <NoteSheet note={note} paperMode onChange={() => {}} onDelete={() => {}} />
          </div>
        ))}
      </div>

      {exporting && (
        <div className="print-hide fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 px-4">
          <div className="flex w-full max-w-[320px] flex-col items-center gap-3 rounded-lg border border-background-200 bg-white p-6 text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-600"></i>
            <p className="text-sm font-bold text-foreground-900">PDF를 만들고 있어요...</p>
            <p className="text-xs text-foreground-500">
              오답노트 {pdfQueue.length}개를 내보내는 중이에요. 잠시만 기다려 주세요.
            </p>
          </div>
        </div>
      )}

      {exportError && (
        <div className="print-hide fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 px-4">
          <div className="w-full max-w-[380px] rounded-lg border border-background-200 bg-white p-6">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-lg"></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-950">PDF 내보내기에 실패했어요</h3>
            </div>
            <p className="mt-3 break-words text-sm leading-relaxed text-foreground-600">{exportError}</p>
            <button
              type="button"
              onClick={() => setExportError(null)}
              className="mt-5 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </>
  );

  /* ── 1단계: 학생 파일 목록 (선생님/운영자) ── */
  if (isTeacher && !openGroup) {
    return (
      <div>
        <div className="print-hide mb-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-white p-4 md:flex-row md:items-center md:justify-between md:px-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
              <i className="ri-folders-line text-base text-primary-600"></i>
              오답노트 파일
            </h2>
            <p className="text-xs text-foreground-500">
              학생 {groups.length}명 · 오답 {notes.length}개 · 파일을 클릭해서 열어보세요
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => openPrint(notes)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2 text-xs font-bold text-accent-800 transition hover:bg-accent-100"
            >
              <i className="ri-printer-fill text-base"></i>
              전체 인쇄
            </button>
            <button
              type="button"
              onClick={() => startExport(notes, buildAllLabel(notes))}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3.5 py-2 text-xs font-bold text-background-50 transition hover:bg-secondary-600"
            >
              <i className="ri-file-pdf-2-line text-base"></i>
              전체 PDF 내보내기
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ kind: 'all' })}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-bold text-foreground-700 transition hover:border-accent-300 hover:bg-accent-50 hover:text-accent-800"
            >
              <i className="ri-delete-bin-6-line text-base"></i>
              전체 삭제
            </button>
          </div>
        </div>

        <div className="print-hide">
          <StudentFileList groups={groups} onOpen={openFile} onDeleteFile={requestDeleteFile} />
        </div>

        <PrintArea notes={printQueue} />
        {confirmModal}
        <PrintGuideModal
          open={pendingPrint !== null}
          count={pendingPrint?.length ?? 0}
          onConfirm={confirmPrint}
          onCancel={() => setPendingPrint(null)}
        />
        {exportLayer}
      </div>
    );
  }

  /* ── 2단계: 열린 학생 파일 (시험 선택 · 인쇄) ── */
  const groupNotesOfGroup = openGroup ? openGroup.exams.flatMap((exam) => exam.notes) : [];
  const allSelectedInGroup =
    !!openGroup && openGroup.exams.length > 0 && examSelKeys(openGroup).every((k) => selectedKeys.has(k));

  return (
    <div>
      {openGroup && (
        <>
          <div className="print-hide mb-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-white p-4 md:flex-row md:items-center md:justify-between md:px-5">
            <div className="flex min-w-0 items-center gap-3">
              {isTeacher && (
                <button
                  type="button"
                  onClick={closeFile}
                  className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-100"
                  aria-label="파일 목록으로"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                </button>
              )}
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-folder-open-fill text-xl"></i>
              </span>
              <div className="min-w-0 leading-tight">
                <h2 className="truncate font-heading text-base font-bold text-foreground-950">
                  {openGroup.studentName}
                </h2>
                <p className="text-xs text-foreground-500">
                  시험 {openGroup.exams.length}개 · 오답 {openGroup.noteCount}개
                  {selectedKeys.size > 0 && (
                    <span className="ml-1 font-bold text-primary-700">
                      (시험 {selectedKeys.size}개 · 오답 {selectedNotes.length}개 선택)
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => toggleAllInGroup(openGroup)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-bold text-foreground-700 transition hover:bg-background-100"
              >
                <i className={`${allSelectedInGroup ? 'ri-checkbox-multiple-line' : 'ri-checkbox-blank-line'} text-base`}></i>
                {allSelectedInGroup ? '전체 해제' : '전체 선택'}
              </button>
              <button
                type="button"
                onClick={() => openPrint(selectedNotes)}
                disabled={selectedKeys.size === 0}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3.5 py-2 text-xs font-bold text-background-50 transition hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-printer-line text-base"></i>
                선택 인쇄{selectedKeys.size > 0 ? ` (${selectedNotes.length})` : ''}
              </button>
              <button
                type="button"
                onClick={() => startExport(selectedNotes, buildExportLabel(selectedNotes))}
                disabled={selectedKeys.size === 0}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3.5 py-2 text-xs font-bold text-background-50 transition hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-file-pdf-2-line text-base"></i>
                선택 PDF{selectedKeys.size > 0 ? ` (${selectedNotes.length})` : ''}
              </button>
              <button
                type="button"
                onClick={() => openPrint(groupNotesOfGroup)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2 text-xs font-bold text-accent-800 transition hover:bg-accent-100"
              >
                <i className="ri-printer-fill text-base"></i>
                이 파일 전체 인쇄
              </button>
              <button
                type="button"
                onClick={() => startExport(groupNotesOfGroup, buildExportLabel(groupNotesOfGroup))}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-secondary-50 px-3.5 py-2 text-xs font-bold text-secondary-800 transition hover:bg-secondary-100"
              >
                <i className="ri-file-pdf-2-line text-base"></i>
                이 파일 전체 PDF
              </button>
            </div>
          </div>

          <div className="print-hide">
            <StudentNotesGroup
              group={openGroup}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              onToggleExpand={toggleExpand}
              onToggleSelect={toggleSelect}
              onDeleteExam={requestDeleteExam}
              onChangeNote={onChangeNote}
              onDeleteNote={onDeleteNote}
            />
          </div>
        </>
      )}

      {/* 인쇄 전용 영역 */}
      <PrintArea notes={printQueue} />

      {confirmModal}
      <PrintGuideModal
        open={pendingPrint !== null}
        count={pendingPrint?.length ?? 0}
        onConfirm={confirmPrint}
        onCancel={() => setPendingPrint(null)}
      />
      {exportLayer}
    </div>
  );
}